import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/state/store/auth';
import { useGetTemplateById } from '@/modules/templates/hooks/use-templates';
import {
  TypeformBuilder,
  type ScreenWithFields,
} from '@/modules/admin/templates/builder/typeform-builder';

const OrgTemplateBuilderPage = () => {
  const { id } = useParams<{ orgId: string; id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { orgId } = useAuthStore();
  const { data: template, isLoading, error } = useGetTemplateById(id);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[hsl(var(--bg))]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-selise-blue" />
          <p className="text-sm text-globe-grey">{t('TEMPLATEBUILDER_LOADING')}</p>
        </div>
      </div>
    );
  }

  if (error || !template || !orgId) {
    return (
      <div className="h-screen flex items-center justify-center bg-[hsl(var(--bg))]">
        <div className="text-center max-w-md p-8">
          <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
            <svg
              className="h-8 w-8 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-[hsl(var(--fg))] mb-2">
            {(error as Error)?.message || t('TEMPLATEBUILDER_TEMPLATENOTFOUND')}
          </h2>
          <p className="text-sm text-globe-grey mb-6">{t('TEMPLATEBUILDER_UNABLETOLOAD')}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-selise-blue text-white font-medium hover:bg-oxford-blue transition-colors"
          >
            {t('TEMPLATEBUILDER_GOBACK')}
          </button>
        </div>
      </div>
    );
  }

  const builderTemplate = {
    id: template.id,
    title: template.title,
    slug: template.slug,
    description: template.description ?? '',
    available: template.available ?? false,
    organizationId: template.organizationId,
  };

  const initialScreens: ScreenWithFields[] = template.screens.map((screen) => ({
    ...screen,
    fields: screen.fields,
  }));

  return <TypeformBuilder template={builderTemplate} initialScreens={initialScreens} />;
};

export default OrgTemplateBuilderPage;
